/**
 * Auth Context Provider
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { authApi, setAuthToken, API_BASE } from '../services/api';
import { getErrorMessage } from '../lib/utils';
import { themes } from '../themes';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buildInfo, setBuildInfo] = useState(null);
  // docs/futuregoals.md FG-8: was `|| 'light'` — a visitor with no stored
  // preference yet (a genuinely first-ever visit to this browser, signed in
  // or not) always got a hardcoded light theme regardless of their OS
  // setting. Falls back to a real prefers-color-scheme check instead,
  // mirroring the exact obsidian/light pair the first-time-login effect
  // below already uses — so the guessed default never contradicts itself
  // depending on whether the visitor happens to be logged in yet. A
  // returning signed-in user's real `preferred_theme` still always wins
  // once it loads (the effect below), on any device — this only fills the
  // gap before that data exists at all.
  const [theme, setTheme] = useState(() => {
    const stored = localStorage.getItem('app_theme');
    if (stored) return stored;
    const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    return prefersDark ? 'obsidian' : 'light';
  });

  // Apply theme to document
  useEffect(() => {
    const root = window.document.documentElement;

    // Clean up previous classes (excluding Tailwind's dark which we manage separately below)
    const allClasses = Object.keys(themes);
    root.classList.remove(...allClasses, 'dark');

    // Get the theme configuration
    const activeTheme = themes[theme] || themes.light;

    // Apply the base class identifier
    root.classList.add(theme);

    // Apply the tailwind 'dark' class if the theme requires it
    if (activeTheme.className.includes('dark')) {
      root.classList.add('dark');
    }

    // Apply dynamic CSS variables
    if (activeTheme.variables) {
      Object.entries(activeTheme.variables).forEach(([key, value]) => {
        root.style.setProperty(key, value);
      });
    }

    localStorage.setItem('app_theme', theme);
  }, [theme]);

  const checkingSessionRef = React.useRef(false);

  const checkExistingSession = useCallback(async () => {
    if (checkingSessionRef.current) return 'checking';

    checkingSessionRef.current = true;
    try {
      const response = await authApi.getMe({ silent: true });
      setUser(response.data);
      return 'ok';
    } catch (error) {
      const currentToken = localStorage.getItem('auth_token');
      const status = error.response?.status;

      if (status === 401 || status === 403) {
        // Definitive rejection — token/cookie is invalid or expired
        console.warn("Session invalid (401/403), clearing logical token.");
        if (currentToken) setAuthToken(null);
        setUser(null);
        return 'invalid';
      }

      // Any other error (500, network error, timeout) — treat as transient.
      // Do NOT clear the token. The user may be able to recover on next load.
      console.warn(`Session check failed with ${status ?? 'network error'} — keeping token, not redirecting.`);
      return 'transient_error';
    } finally {
      checkingSessionRef.current = false;
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      // 1. Restore auth_token headers if one exists in localStorage
      const token = localStorage.getItem('auth_token');
      if (token) {
        setAuthToken(token);
      }

      // 2. Unconditionally ping the backend. 
      // If we have an HttpOnly cookie but an empty localStorage, we want this to automatically restore us.
      const result = await checkExistingSession();

      // If the session check hit a transient error (5xx / network),
      // we can't confirm the user — but we also can't definitively reject them.
      // Keep them logged in if we have a valid token structure, and let the
      // next action (navigation, API call) surface the real error.
      if (result === 'transient_error') {
        // Re-try once after a brief pause to handle race conditions on boot
        await new Promise(r => setTimeout(r, 1500));
        await checkExistingSession();
      }

      // Only hide the loader after the auth checks finish natively
      setLoading(false);
    };

    initAuth();
  }, [checkExistingSession]);


  useEffect(() => {
    const fetchBuildInfo = async () => {
      try {
        const res = await authApi.getBuildInfo();
        setBuildInfo(res.data);
      } catch (e) {
        // no-op
      }
    };
    fetchBuildInfo();
  }, []);

  const login = async (username, password) => {
    // finding D-03: this used to log the raw bearer token to the console
    // (`console.log('[AuthContext] Extracted access_token:', access_token)`),
    // plus the full axios response and error objects. react-scripts does NOT
    // strip console.log from production builds, and craco.config.js added no
    // such transform, so these shipped to production — combined with finding
    // C-07 (no token revocation at the time), a token captured this way from
    // devtools/a shared machine was valid for the token's full lifetime with
    // no way to invalidate it early.
    try {
      const response = await authApi.login(username, password);
      const { access_token } = response.data;
      setAuthToken(access_token);
      await checkExistingSession();
      toast.success('Logged in successfully');
      return true;
    } catch (error) {
      toast.error(getErrorMessage(error, 'Login failed'));
      return false;
    }
  };

  const register = async (username, password, email, displayName, channelName, marketingConsent) => {
    try {
      const response = await authApi.register(username, password, email, displayName, channelName, marketingConsent);
      const { access_token } = response.data;
      setAuthToken(access_token);
      await checkExistingSession();
      toast.success('Account created successfully. Please check your email to verify.');
      return true;
    } catch (error) {
      toast.error(getErrorMessage(error, 'Registration failed'));
      return false;
    }
  };

  const verifyEmail = async (token) => {
    try {
      await authApi.verifyEmail(token);
      await checkExistingSession();
      toast.success('Email verified successfully');
      return true;
    } catch (error) {
      toast.error(getErrorMessage(error, 'Verification failed'));
      return false;
    }
  };

  const resendVerification = async () => {
    try {
      await authApi.resendVerification();
      toast.success('Verification email resent');
      return true;
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to resend verification'));
      return false;
    }
  };

  const forgotPassword = async (email) => {
    try {
      await authApi.forgotPassword(email);
      toast.info('If that email exists, a reset link has been sent');
      return true;
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send reset link'));
      return false;
    }
  };

  const resetPassword = async (token, password) => {
    try {
      await authApi.resetPassword(token, password);
      toast.success('Password updated successfully. Please log in.');
      return true;
    } catch (error) {
      toast.error(getErrorMessage(error, 'Password reset failed'));
      return false;
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setAuthToken(null);
    }
  };

  const handleOAuthCallback = async (token) => {
    try {
      if (token) setAuthToken(token);
      // Directly fetch session to bypass checkExistingSession's early exit ref
      // The cookie set by backend will authenticate us automatically
      const response = await authApi.getMe();
      setUser(response.data);
      toast.success('Logged in successfully');
      return true;
    } catch (error) {
      toast.error('OAuth login failed');
      return false;
    }
  };

  const updateProfile = async (updates) => {
    try {
      await authApi.updateProfile(updates);
      await checkExistingSession();
      toast.success('Profile updated successfully');
      return true;
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update profile'));
      return false;
    }
  };

  const completeOnboarding = async () => {
    try {
      await authApi.updateProfile({ has_completed_onboarding: true });
      await checkExistingSession();
      return true;
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
      return false;
    }
  };

  // ── Auto Detect Theme logic ───────────────────────────────────────────────
  useEffect(() => {
    if (user && !user.has_set_initial_theme) {
      // Logic only runs once for first-time login
      const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const initialTheme = isDarkMode ? 'obsidian' : 'light';

      // Update local state for immediate feedback
      setTheme(initialTheme);
      
      // Persist to backend
      authApi.updateProfile({ 
        preferred_theme: initialTheme, 
        has_set_initial_theme: true 
      }).then(() => {
        // Refresh user data to sync has_set_initial_theme
        checkExistingSession();
      }).catch(err => {
        console.error('Failed to save initial theme:', err);
      });
    } else if (user && user.preferred_theme && user.preferred_theme !== theme) {
      // Sync theme from backend if different
      setTheme(user.preferred_theme);
    }
  }, [user?.id, user?.has_set_initial_theme, user?.preferred_theme]);
  // ─────────────────────────────────────────────────────────────────────────

  const getToken = () => {
    return localStorage.getItem('auth_token');
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      logout,
      updateProfile,
      verifyEmail,
      resendVerification,
      forgotPassword,
      resetPassword,
      handleOAuthCallback,
      buildInfo,
      theme,
      setTheme,
      getToken,
      completeOnboarding
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
